import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { normalizeJoinCode } from '../../quizLive/normalizeJoinCode';
import { validateLiveSessionByPin } from '../../quizLive/validateLiveSession';
import { appPath } from '../../utils/appRouting';

type JoinState =
  | { status: 'idle' }
  | { status: 'resolving' }
  | { status: 'resolved'; sessionId: string }
  | { status: 'error'; message: string };

export default function JoinLiveRoute() {
  const navigate = useNavigate();
  const { code: rawCode } = useParams<{ code: string }>();
  const [searchParams] = useSearchParams();

  const code = useMemo(() => normalizeJoinCode(rawCode || '').slice(0, 6), [rawCode]);
  const nickname = useMemo(() => {
    const n = (searchParams.get('n') || '').trim();
    return n ? n.slice(0, 20) : '';
  }, [searchParams]);

  const [state, setState] = useState<JoinState>({ status: 'idle' });

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!/^[A-Z0-9]{6}$/.test(code)) {
        setState({ status: 'error', message: 'Codice non valido. Deve essere di 6 caratteri (A-Z, 0-9).' });
        return;
      }

      try {
        if (nickname) {
          try {
            window.localStorage.setItem('live_quiz_nickname', nickname);
          } catch {
            // ignore
          }
        }

        setState({ status: 'resolving' });

        const validated = await validateLiveSessionByPin(code, { requireStarted: false, timeoutMs: 8000 });
        if (!validated.ok) throw new Error(validated.message);
        const sessionId = validated.sessionId;
        if (!sessionId) throw new Error('Sessione non trovata. Riprova tra qualche secondo.');
        if (cancelled) return;

        setState({ status: 'resolved', sessionId });
        const q = nickname ? `?n=${encodeURIComponent(nickname)}` : '';
        navigate(appPath(`/live/s/${sessionId}${q}`), { replace: true });
      } catch (e: any) {
        if (cancelled) return;
        setState({ status: 'error', message: e?.message || 'Sessione non trovata. Riprova tra qualche secondo.' });
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [code, nickname, navigate]);

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6">
        <div className="text-lg font-semibold">Connessione in corso</div>
        <div className="mt-1 text-sm text-muted-foreground">Codice: <span className="font-mono">{code || '—'}</span></div>

        {state.status === 'resolving' && (
          <div className="mt-4 text-sm">Sto cercando la sessione…</div>
        )}

        {state.status === 'error' && (
          <div className="mt-4">
            <div className="text-sm text-destructive">{state.message}</div>
            <button
              type="button"
              className="mt-4 w-full px-4 py-2 rounded-xl bg-primary text-primary-foreground"
              onClick={() => navigate(appPath('/live/join'))}
            >
              Inserisci un altro codice
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
